import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useArticles } from '../contexts/ArticleContext';
import ArticleCard from '../components/molecules/ArticleCard';
import WorldMap from '../components/WorldMap';
import Navigation from '../components/Navigation';
import ImageSlider from '../components/ImageSlider';
import Profile from '../components/Profile';
import SEO from '../components/SEO';
import { TOP_SLIDER_IMAGES, TOP_SLIDER_LINKS } from '../constants/images';
import {
  Container,
  SectionTitle,
  Section,
  SectionDescription
} from '../styles/commonStyles';
import styled from 'styled-components';
import { theme } from '../styles/theme';
const PageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  background-color: ${theme.colors.primary};
  
  & > * {
    width: 100%;
    box-sizing: border-box;
  }
`;

// SVG区切りのスタイル
const WaveDivider = styled.div<{ image: string }>`
  position: relative;
  height: 120px;
  width: 100vw;
  max-width: 100%;
  background: url(${props => props.image}) no-repeat;
  background-size: 100% 100%;
  display: block;
  margin: 0;
  padding: 0;
  vertical-align: bottom;
  z-index: 2;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 80px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 60px;
  }
`;

const StyledWorldMap = styled(WorldMap)`
  height: 600px;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    height: 500px;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 400px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 300px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;  
  min-height: 300px;
  width: 100%;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid ${props => props.theme.colors.primary};
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-right: 16px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: 18px;
  color: #666;
`;

const ArticlesContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const ArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  margin-bottom: ${props => props.theme.space.lg};
  
  & > div {
    margin: 0 auto;
    width: 100%;
    max-width: 800px;
  }
`;

const ArticleBadge = styled.div`
  background-color: #D67616;
  color: white;
  font-size: 12px;
  font-weight: bold;
  padding: 4px 8px;
  display: inline-block;
  position: absolute;
  top: 5px;
  left: 8%;
  z-index: 2;
  border-radius: 12px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    left: 0%;
    top: 0px;
  }
`;

const ViewAllButton = styled.button`
  background-color: #D67616;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 30px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
  display: block;
  margin: 30px auto 0;
  
  &:hover {
    background-color: #e55a00;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-top: -40px;
  }
`;

const BlueSection = styled(Section)`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.space.md} 0 0 0;
  position: relative;
  z-index: 1;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  
  ${SectionTitle} {
    color: white;
    text-align: center;
    margin-bottom: 30px;
    font-size: 1.5rem;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 100px;
      height: 2px;
      background-color: white;
    }
  }
  
  ${SectionDescription} {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const GreenSection = styled(Section)`
  background-color: ${props => props.theme.colors.secondary};
  color: white;
  padding: ${props => props.theme.space.xl} 0 0 0;
  position: relative;
  z-index: 1;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  
  ${SectionTitle} {
    color: white;
    text-align: center;
    margin-bottom: 30px;
    font-size: 1.5rem;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 100px;
      height: 2px;
      background-color: white;
    }
  }
  
  ${SectionDescription} {
    color: rgba(255, 255, 255, 0.8);
    text-align: center;
    margin-bottom: 20px;
  }
`;

const WhiteSection = styled(Section)`
  background-color: white;
  position: relative;
  z-index: 1;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  padding-top: 0;
  margin-top: -60px;
`;

const MascotCharacter = styled.div`
  position: absolute;
  bottom: -50px;
  right: 300px;
  width: 150px;
  height: 150px;
  background: url('/images/arayottokun.svg') no-repeat;
  background-size: contain;
  z-index: 5;
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    right: 200px;
    width: 130px;
    height: 130px;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    right: 100px;
    width: 120px;
    height: 120px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`;

const MascotSpeechBubble = styled.div`
  position: absolute;
  bottom: 60px;
  right: 100px;
  background-color: white;
  border-radius: 20px;
  padding: 10px 15px;
  font-size: 14px;
  color: #333;
  z-index: 5;
  max-width: 200px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 10px;
    left: -8px;
    border-width: 10px 10px 10px 0;
    border-style: solid;
    border-color: transparent white transparent transparent;
  }
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    right: 50px;
    max-width: 180px;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    right: 30px;
    max-width: 150px;
    font-size: 13px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`;

// 最大表示記事数を3件に制限
const MAX_ARTICLES = 3;

const HomePage: React.FC = () => {
  const { articles, loading } = useArticles();
  
  // 新着記事を日付順にソートし、最大3件に制限
  const latestArticles = [...articles]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, MAX_ARTICLES);

  // is_topプロパティがtrueの記事を抽出
  const topArticles = articles.filter(article => article.is_top);
  
  // トップ記事の画像とリンクを生成
  const topArticleImages = topArticles.length > 0 
    ? topArticles.map(article => `/articles/${article.id}/cover.webp`)
    : TOP_SLIDER_IMAGES;
    
  const topArticleLinks = topArticles.length > 0
    ? topArticles.map(article => `/article/${article.slug}`)
    : TOP_SLIDER_LINKS;

  return (
    <PageWrapper>
      <SEO 
        title="ホーム"
        description="アラヨットラベル - 世界中の旅行情報、観光スポット、グルメ情報を提供する旅行ブログです。新着記事や地図から旅行先を探せます。"
        keywords="旅行, 観光, 海外旅行, 国内旅行, 旅行ブログ, アラヨットラベル, 世界地図"
        canonical="/"
      />
      <ImageSlider 
        images={topArticleImages} 
        links={topArticleLinks}
      />
      <Navigation />
      
      <BlueSection>
        <Container>
          <SectionTitle>新着記事</SectionTitle>
          {loading && articles.length === 0 ? (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>記事を読み込み中です...</LoadingText>
            </LoadingContainer>
          ) : (
            <ArticlesContainer>
              <ArticlesGrid>
                {latestArticles.map((article, index) => (
                  <div key={article.slug || index} style={{ position: 'relative' }}>
                    {index === 0 && <ArticleBadge>NEW</ArticleBadge>}
                    <ArticleCard 
                      article={article}
                    />
                  </div>
                ))}
              </ArticlesGrid>
              {loading && articles.length > 0 && (
                <LoadingContainer>
                  <LoadingSpinner />
                  <LoadingText>記事を読み込み中...</LoadingText>
                </LoadingContainer>
              )}
              <div style={{ position: 'relative' }}>
                <Link to="/articles" onClick={() => window.scrollTo(0, 0)}>
                  <ViewAllButton>
                    もっと見る
                  </ViewAllButton>
                </Link>
                <MascotCharacter />
                <MascotSpeechBubble>
                  次はどの国に行こうかな？
                </MascotSpeechBubble>
              </div>
            </ArticlesContainer>
          )}
        </Container>
      </BlueSection>

      <WaveDivider image="/images/blue-to-green-wave.svg" />

      <GreenSection>
        <Container>
          <SectionTitle>地図から探す</SectionTitle>
          <SectionDescription>
            行ったことのある国に色がついています。<br />
            国名をクリックすると関連する記事にリンクします。
          </SectionDescription>
          <StyledWorldMap />
        </Container>
      </GreenSection>

      <WaveDivider image="/images/green-to-white-wave.svg" />

      <WhiteSection>
        <Container>
          <Profile />
        </Container>
      </WhiteSection>

    </PageWrapper>
  );
};

export default HomePage;
